/* eslint-disable no-magic-numbers , complexity */
import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { syncNowOauth2Connection } from '@futrli/organisations';
import {
  DATASOURCES_STATUS,
  isInPendingSetup,
  isInSyncFailure,
  isInSyncing,
  isInSyncInitialFailure
} from '@futrli/shared-services';
import { UpdateFromExcelModalContent } from '@futrli/shared-services/excel-upload';
import { useIntegrations } from '@futrli/predict/predict-management/hooks/useIntegrations';
import { useIsExcelOrganisation } from '@futrli/predict';
import { OrganisationUploadDrawer } from '@futrli/shared-services/excel-upload/excel-modals/upload/organisation-upload-drawer';
import { UpdateFromExcelOrgStatus } from '@futrli/shared-services/excel-upload/excel-modals/update/update-from-excel-org-status';

import {
  SyncControlDatasourceState,
  SyncControlState
} from '../../forecast/detail/reports/datasource-sync/sync-control/types';
import { getLatestSyncTimestamp } from '../../forecast/detail/reports/datasource-sync/sync-control/utils';
import { useDatasourcePolling } from '../../forecast/detail/reports/datasource-sync/sync-control/hooks/use-datasource-polling';
import { useOrganisationIntegrationByIds } from '../../predict-management/hooks/useOrganisationIntegrationByIds';
import { useReconnectDatasourceByIds } from '../../hooks/use-reconnect-datasource-by-ids';
import { useUpdateReduxOrgSyncStatus } from '../../forecast/detail/reports/datasource-sync/sync-control/hooks/use-update-redux-org-sync-status';
import { useLoadFinancialConfig } from '../../forecast/prediction-builder/hooks/useLoadFinancialConfig/useLoadFinancialConfig';
import { DatasourceReconcileDrawer } from '../../forecast/detail/reports/datasource-sync/datasource-reconcile-drawer';
import { DEFAULT_SYNC_CONTEXT, extractIdsFromPathname } from './utils';
import { SyncContextType, SyncResultType } from './types';

// Re-export from the lightweight core file so existing consumers continue to work
import { SyncContextInternalProvider, useSyncContext } from './sync-context-core';
export { SyncContextInternalProvider, useSyncContext };

interface SyncContextProviderProps {
  children: ReactNode;
}

/**
 * Provides shared syncing state to all descendants.
 * Uses extractIdsFromPathname() on the current URL to extract workspace/org IDs.
 * On consolidation routes or when IDs are absent, wraps children with a static
 * DEFAULT_SYNC_CONTEXT so that useSyncContext() never throws.
 */
export const SyncContextProvider = ({ children }: SyncContextProviderProps) => {
  const { pathname } = useLocation();
  const { workspaceId, organisationId, isConsolidation } = extractIdsFromPathname(pathname);

  // When IDs are missing or the route is a consolidation, provide the static default context
  // so that useSyncContext() works safely without mounting SyncContextProviderInner
  // (which would trigger data-fetching hooks, polling, etc.).
  if (!workspaceId || !organisationId || isConsolidation) {
    return <SyncContextInternalProvider value={DEFAULT_SYNC_CONTEXT}>{children}</SyncContextInternalProvider>;
  }

  return (
    <SyncContextProviderInner workspaceId={workspaceId} organisationId={organisationId}>
      {children}
    </SyncContextProviderInner>
  );
};

// ---------------------------------------------------------------------------
// Inner provider (only mounted when IDs are present)
// ---------------------------------------------------------------------------

interface SyncContextProviderInnerProps {
  workspaceId: string;
  organisationId: string;
  children: ReactNode;
}

const SyncContextProviderInner = ({ workspaceId, organisationId, children }: SyncContextProviderInnerProps) => {
  const { organisation, integration } = useOrganisationIntegrationByIds(workspaceId, organisationId);
  const { updateOrgSyncStatus } = useUpdateReduxOrgSyncStatus(workspaceId, organisationId);
  const { reconnect, isReconnecting } = useReconnectDatasourceByIds(
    workspaceId,
    organisationId,
    organisation?.datasource,
    integration
  );
  const { data: financialConfig } = useLoadFinancialConfig(workspaceId, organisationId);

  const isExcelOrganisation = useIsExcelOrganisation(workspaceId, organisationId);

  const integrations = useIntegrations();

  const [hasOauth2Error, setHasOauth2Error] = useState(false);
  const [needsReconnect, setNeedsReconnect] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isExcelDrawerOpen, setIsExcelDrawerOpen] = useState(false);

  const [syncResultType, setSyncResultType] = useState<SyncResultType | null>(null);

  const openDrawer = useCallback(() => (isExcelOrganisation ? setIsExcelDrawerOpen(true) : setIsDrawerOpen(true)), [
    isExcelOrganisation
  ]);
  const closeDrawer = useCallback(() => (isExcelOrganisation ? setIsExcelDrawerOpen(false) : setIsDrawerOpen(false)), [
    isExcelOrganisation
  ]);

  const { datasource, error, excelFiles, isLoading: isLoadingDatasource, loadData, setPolling } = useDatasourcePolling(
    workspaceId,
    organisationId
  );

  const syncTimestamp = getLatestSyncTimestamp(datasource, excelFiles);
  const previousSyncTimestamp = useRef('');

  // Compute high-level control state
  const syncControlState = useMemo(() => {
    if (!isSyncing) {
      if (isLoadingDatasource && error) {
        return SyncControlState.LOAD_ERROR;
      }
      if (isLoadingDatasource) {
        return SyncControlState.LOADING;
      }
    }
    return SyncControlState.READY;
  }, [isSyncing, isLoadingDatasource, error]);

  // Compute datasource-level state
  const datasourceState = useMemo(() => {
    if (datasource) {
      if (isSyncing || isInSyncing(datasource)) {
        return SyncControlDatasourceState.SYNCING;
      }
      if (isReconnecting) {
        return SyncControlDatasourceState.RECONNECTING;
      }
      if (isInPendingSetup(datasource)) {
        return SyncControlDatasourceState.PENDING_SETUP;
      }
      if (isInSyncFailure(datasource) || isInSyncInitialFailure(datasource) || hasOauth2Error) {
        return SyncControlDatasourceState.SYNC_FAILED;
      }
      return SyncControlDatasourceState.SYNC_COMPLETE;
    }
    return SyncControlDatasourceState.NOT_CONNECTED;
  }, [datasource, isSyncing, isReconnecting, hasOauth2Error]);

  // Handle when sync is already in progress
  useEffect(() => {
    if (!isSyncing && datasource && isInSyncing(datasource)) {
      previousSyncTimestamp.current = syncTimestamp;
      setPolling(true);
      setIsSyncing(true);
    }
  }, [datasource, isSyncing, setPolling, syncTimestamp]);

  // Handle syncing finished
  useEffect(() => {
    if (isSyncing && datasource && !isInSyncing(datasource) && syncTimestamp !== previousSyncTimestamp.current) {
      setIsSyncing(false);
      setPolling(false);

      updateOrgSyncStatus(datasource);
    }
  }, [datasource, isSyncing, setPolling, syncTimestamp, updateOrgSyncStatus]);

  // Trigger OAuth2 sync-now
  const handleOauth2SyncNow = useCallback(async () => {
    if (organisationId && datasource && workspaceId) {
      try {
        previousSyncTimestamp.current = syncTimestamp;
        setHasOauth2Error(false);
        setNeedsReconnect(false);
        setIsSyncing(true);

        await syncNowOauth2Connection(workspaceId, organisationId, datasource.id);
        await updateOrgSyncStatus({
          ...datasource,
          status: {
            ...datasource.status,
            value: DATASOURCES_STATUS.sync_initial_update_status
          }
        });
        loadData(workspaceId, organisationId);

        setPolling(true);
      } catch (err: unknown) {
        const axiosError = err as { response?: { status?: number; data?: { message?: string; status?: string } } };
        if (
          axiosError?.response?.status === 500 &&
          axiosError?.response?.data?.status === 'ERROR' &&
          axiosError?.response?.data?.message?.toLowerCase().includes('re-authenticate')
        ) {
          setNeedsReconnect(true);
        }
        setHasOauth2Error(true);
        setIsSyncing(false);
      }
    }
  }, [datasource, loadData, organisationId, setPolling, syncTimestamp, updateOrgSyncStatus, workspaceId]);

  const value: SyncContextType = useMemo(
    () => ({
      datasource,
      excelFiles,
      isLoadingDatasource,
      error,
      syncControlState,
      datasourceState,
      isSyncing,
      hasOauth2Error,
      needsReconnect,
      syncTimestamp,
      handleOauth2SyncNow,
      loadData,
      setPolling,
      isReconnecting,
      reconnect,
      workspaceId,
      organisationId,
      organisation,
      integration,
      financialConfig,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      syncResultType,
      setSyncResultType
    }),
    [
      datasource,
      excelFiles,
      isLoadingDatasource,
      error,
      syncControlState,
      datasourceState,
      isSyncing,
      hasOauth2Error,
      needsReconnect,
      syncTimestamp,
      handleOauth2SyncNow,
      loadData,
      setPolling,
      isReconnecting,
      reconnect,
      workspaceId,
      organisationId,
      organisation,
      integration,
      financialConfig,
      isDrawerOpen,
      openDrawer,
      closeDrawer,
      syncResultType,
      setSyncResultType
    ]
  );

  return (
    <SyncContextInternalProvider value={value}>
      {children}
      <DatasourceReconcileDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        unreconciledTransactionsCount={financialConfig?.unreconciled_transactions_count}
      />
      <OrganisationUploadDrawer
        title={<FormattedMessage id="predict.update_data" defaultMessage="Update data" />}
        isOpen={isExcelDrawerOpen}
        handleClose={closeDrawer}
        workspaceId={workspaceId}
        excelIntegration={integrations?.find(integration => integration.slug === 'excel')}
        organisation={organisation || undefined}
        isUpdateDrawer
      >
        <>
          <UpdateFromExcelOrgStatus name={organisation?.name || ''} lastSynced={syncTimestamp || ''} />
          <UpdateFromExcelModalContent
            workspaceId={workspaceId}
            organisationId={organisation?.id || ''}
            config={{ entityType: 'organisation' }}
            guidelinesUrl="https://support.futrli.com/en/articles/10485548-excel-organisations"
          />
        </>
      </OrganisationUploadDrawer>
    </SyncContextInternalProvider>
  );
};
